/* eslint-disable max-lines-per-function */
import { GraphQLError } from "graphql";
import { flow } from "lodash";
import Papa from "papaparse";

import {
  Problem,
  DynamicProblemSearcher,
  verifyDateFormat,
  verifyPositionOrder,
  verifyRequiredFields,
  verifySchemas,
  verifyPhoneNumbers,
  verifyContactInformation,
  verifyEmails,
  verifyDeniedAndMovedIn,
} from "../validate";
import {
  adjustDatetimes,
  appendNotes,
  castValues,
  conformStructure,
  keyedValues,
  persistUnsupportedValues,
  removeEmptyColumns,
  removeEmptyRecords,
  trimData,
} from "../transform";
import { Payload, Record } from "../types";

// preprocessor is a function that takes a Payload and returns a new Payload,
// transformed into the desired dataset that will be used to display the source
// column fields and perform validation on.
export const csvPreprocessor = flow(
  trimData,
  removeEmptyColumns(false),
  removeEmptyRecords,
  removeEmptyColumns(true),
  keyedValues,
);

// postprocessor is a function that takes a Payload and returns a new Payload,
// transformed into the desired dataset that will be exported to the user or
// imported into the system.
export const csvPostprocessor = flow(
  appendNotes,
  adjustDatetimes,
  persistUnsupportedValues,
  castValues,
  conformStructure,
);

export const validators = [
  verifyContactInformation,
  verifyRequiredFields,
  verifySchemas,
  verifyDateFormat,
  verifyPositionOrder,
  verifyPhoneNumbers,
  verifyEmails,
  verifyDeniedAndMovedIn,
];

// deriveValidationProblems returns the entire set of problems found with the
// given `payload`.
export const deriveValidationProblems: DynamicProblemSearcher = (currentValidators, payload) =>
  currentValidators.reduce((problems, inputFn) =>
    problems.concat(inputFn(payload)), [] as Problem[]);

type ImporterGraphQLError = {
  message: string;
  row: number;
  details: { [k: string]: string[] }
}

export const deriveGraphqlProblems = (
  errors: readonly GraphQLError[],
  payload: Payload<Record>,
): Problem[] => {
  const problems: Problem[] = [];

  // Apollo's errors don't match up with absinthe errors, where absinthe lets you
  // add any data to a Map, so long as it's got a 'message' key/value
  errors.forEach((unknownError: unknown) => {
    const error = unknownError as ImporterGraphQLError;

    if (!error.details) {
      problems.push({
        type: "global",
        key: "general-error",
        data: {
          message: error.message,
        },
      });

      return;
    }

    // validations.tsx adds 2 to the index to skip the headers and account for 1-indexed counting
    // but `error.row` is already the row, not the index, so we have to remove 2 just so validations
    // can add two. Validations is being reused here so it didn't seem to make sense to remove the
    // addition in it and adjust the validators to handle calculating the row each time on their own
    const row = error.row - 2;

    Object.entries(error.details).forEach(([detail, message], index) => {
      const fieldData = Object.entries(payload.mapping)
        .find(([, { field }]) => field === detail);

      const key = `graphql-error-${row}-${index}`;

      if (fieldData) {
        const [csvField] = fieldData;

        const value = payload.data[row][csvField];

        problems.push({
          type: "record",
          key,
          data: {
            field: detail,
            value,
            index: row,
          },
        });
      } else {
        problems.push({
          type: "global",
          key,
          data: {
            message: message.join(", "),
          },
        });
      }
    });
  });

  return problems;
};

const addMissingColumnHeaders = (result: Papa.ParseResult) : Papa.ParseResult => {
  const replacementHeaders = result.data[0].map((element:string, i:number) => (element === "" ? `Import Column ${i}` : element));

  return {
    ...result,
    data: ([replacementHeaders]).concat(result.data.slice(1)),
  };
};

// onUploadCSV returns an event handler which is called with the files selected
// by the user. The first file is then parsed as a CSV string and unmarshaled
// into a string[][] and passed as an argument into "fn".
export const onUploadCSV = (fn: (result: Papa.ParseResult, file: File) => void) => (
  files: FileList | File[] | null,
) => {
  if (files === null || files?.length === 0) {
    return;
  }

  const complete = (result: Papa.ParseResult, file: File) => {
    if (!file) {
      throw new Error("unexpected 'file' value.");
    }

    fn(addMissingColumnHeaders(result), file);
  };

  Papa.parse(files[0], { complete });
};
